import React, { useState, useCallback, useEffect } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Trophy, ArrowUp, ArrowDown, ArrowLeft, ArrowRight, RotateCcw } from "lucide-react";

interface Game2048Props {
  t: (key: string) => string;
}

type Grid = number[][];

const GRID_SIZE = 4;

const getTileColor = (value: number): string => {
  const colors: Record<number, string> = {
    2: "bg-amber-100 text-amber-900",
    4: "bg-amber-200 text-amber-900",
    8: "bg-orange-300 text-white",
    16: "bg-orange-400 text-white",
    32: "bg-orange-500 text-white",
    64: "bg-orange-600 text-white",
    128: "bg-yellow-400 text-white",
    256: "bg-yellow-500 text-white",
    512: "bg-yellow-600 text-white",
    1024: "bg-amber-500 text-white",
    2048: "bg-amber-600 text-white",
  };
  return colors[value] || "bg-amber-700 text-white";
};

const createEmptyGrid = (): Grid => {
  return Array(GRID_SIZE).fill(null).map(() => Array(GRID_SIZE).fill(0));
};

const addRandomTile = (grid: Grid): Grid => {
  const newGrid = grid.map(row => [...row]);
  const emptyCells: [number, number][] = [];
  
  for (let i = 0; i < GRID_SIZE; i++) {
    for (let j = 0; j < GRID_SIZE; j++) {
      if (newGrid[i][j] === 0) {
        emptyCells.push([i, j]);
      }
    }
  }
  
  if (emptyCells.length > 0) {
    const [row, col] = emptyCells[Math.floor(Math.random() * emptyCells.length)];
    newGrid[row][col] = Math.random() < 0.9 ? 2 : 4;
  }
  
  return newGrid;
};

const initializeGrid = (): Grid => {
  let grid = createEmptyGrid();
  grid = addRandomTile(grid);
  grid = addRandomTile(grid);
  return grid;
};

const slideRow = (row: number[]): { newRow: number[]; score: number } => {
  let score = 0;
  const filtered = row.filter(val => val !== 0);
  const newRow: number[] = [];
  
  for (let i = 0; i < filtered.length; i++) {
    if (i < filtered.length - 1 && filtered[i] === filtered[i + 1]) {
      const merged = filtered[i] * 2;
      newRow.push(merged);
      score += merged;
      i++;
    } else {
      newRow.push(filtered[i]);
    }
  }
  
  while (newRow.length < GRID_SIZE) {
    newRow.push(0);
  }
  
  return { newRow, score };
};

const moveLeft = (grid: Grid): { newGrid: Grid; score: number; moved: boolean } => {
  let totalScore = 0;
  let moved = false;
  const newGrid = grid.map(row => {
    const { newRow, score } = slideRow(row);
    totalScore += score;
    if (row.join(',') !== newRow.join(',')) moved = true;
    return newRow;
  });
  return { newGrid, score: totalScore, moved };
};

const rotateGrid = (grid: Grid): Grid => {
  const newGrid = createEmptyGrid();
  for (let i = 0; i < GRID_SIZE; i++) {
    for (let j = 0; j < GRID_SIZE; j++) {
      newGrid[j][GRID_SIZE - 1 - i] = grid[i][j];
    }
  }
  return newGrid;
};

const move = (grid: Grid, direction: 'up' | 'down' | 'left' | 'right'): { newGrid: Grid; score: number; moved: boolean } => {
  let rotatedGrid = [...grid.map(row => [...row])];
  let rotations = 0;
  
  switch (direction) {
    case 'down': rotations = 1; break;
    case 'right': rotations = 2; break;
    case 'up': rotations = 3; break;
    default: rotations = 0;
  }
  
  for (let i = 0; i < rotations; i++) {
    rotatedGrid = rotateGrid(rotatedGrid);
  }
  
  const result = moveLeft(rotatedGrid);
  
  for (let i = 0; i < (4 - rotations) % 4; i++) {
    result.newGrid = rotateGrid(result.newGrid);
  }
  
  return result;
};

const canMove = (grid: Grid): boolean => {
  for (let i = 0; i < GRID_SIZE; i++) {
    for (let j = 0; j < GRID_SIZE; j++) {
      if (grid[i][j] === 0) return true;
      if (j < GRID_SIZE - 1 && grid[i][j] === grid[i][j + 1]) return true;
      if (i < GRID_SIZE - 1 && grid[i][j] === grid[i + 1][j]) return true;
    }
  }
  return false;
};

const hasWon = (grid: Grid): boolean => {
  return grid.some(row => row.some(cell => cell >= 2048));
};

export function Game2048({ t }: Game2048Props) {
  const [grid, setGrid] = useState<Grid>(initializeGrid);
  const [score, setScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [won, setWon] = useState(false);

  const handleMove = useCallback((direction: 'up' | 'down' | 'left' | 'right') => {
    if (gameOver) return;
    
    const result = move(grid, direction);
    if (result.moved) {
      const newGrid = addRandomTile(result.newGrid);
      setGrid(newGrid);
      setScore(s => s + result.score);
      
      if (hasWon(newGrid) && !won) {
        setWon(true);
      }
      
      if (!canMove(newGrid)) {
        setGameOver(true);
      }
    }
  }, [grid, gameOver, won]);

  const resetGame = useCallback(() => {
    setGrid(initializeGrid());
    setScore(0);
    setGameOver(false);
    setWon(false);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (gameOver) return;
      switch (e.key) {
        case 'ArrowUp': handleMove('up'); break;
        case 'ArrowDown': handleMove('down'); break;
        case 'ArrowLeft': handleMove('left'); break;
        case 'ArrowRight': handleMove('right'); break;
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleMove, gameOver]);

  const directionButtons = [
    { direction: 'up' as const, icon: ArrowUp, label: t("up") },
    { direction: 'down' as const, icon: ArrowDown, label: t("down") },
    { direction: 'left' as const, icon: ArrowLeft, label: t("left") },
    { direction: 'right' as const, icon: ArrowRight, label: t("right") },
  ];

  return (
    <div className="space-y-6" dir="ltr">
      <div className="flex items-center justify-between">
        <div className="text-2xl font-bold text-primary">{t("score")}: {score}</div>
        <Button
          onClick={resetGame}
          variant="outline"
          className="gap-2"
          data-scan-item
        >
          <RotateCcw className="w-4 h-4" />
          {t("newGame")}
        </Button>
      </div>

      {(won || gameOver) && (
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          className={cn(
            "flex items-center justify-center gap-3 p-4 rounded-xl border",
            won ? "bg-success/20 border-success" : "bg-destructive/20 border-destructive"
          )}
        >
          <Trophy className={cn("w-6 h-6", won ? "text-success" : "text-destructive")} />
          <span className={cn("text-xl font-bold", won ? "text-success" : "text-destructive")}>
            {won ? t("youWon") : t("gameOver")} - {t("score")}: {score}
          </span>
        </motion.div>
      )}

      <div className="bg-muted/50 rounded-xl p-3 max-w-xs mx-auto">
        <div className="grid grid-cols-4 gap-2">
          {grid.flat().map((value, index) => (
            <motion.div
              key={index}
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className={cn(
                "aspect-square rounded-lg flex items-center justify-center font-bold text-lg",
                value === 0 ? "bg-muted" : getTileColor(value)
              )}
            >
              {value > 0 && value}
            </motion.div>
          ))}
        </div>
      </div>

      <div className="flex justify-center gap-3 p-2 rounded-xl transition-all">
        {directionButtons.map((btn) => {
          const Icon = btn.icon;
          return (
            <Button
              key={btn.direction}
              onClick={() => handleMove(btn.direction)}
              size="lg"
              className="p-4"
              disabled={gameOver}
              data-scan-item
            >
              <Icon className="w-6 h-6" />
            </Button>
          );
        })}
      </div>
    </div>
  );
}
